import React, { useEffect, useState } from 'react';
import { Box, TextField, Button } from '@mui/material';
import { Controller, useFormContext, Path } from 'react-hook-form';
import { WizardFormData } from '../schemas';
import { useWizardForm } from '../hooks/useWizardForm';
import { useWizardData } from '../hooks/useWizardData';

type WizardUrlFieldWithParametersProps = {
  label: string;
  name: Path<WizardFormData>;
  helperText?: string;
};

export const WizardUrlFieldWithParameters = ({ label, name, helperText = '' }: WizardUrlFieldWithParametersProps) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useFormContext<WizardFormData>();
  const { trafficSources } = useWizardData();

  const { getRedirectUrl } = useWizardForm();

  const [paramInjected, setParamInjected] = useState<Map<string, boolean>>(new Map());
  const [detectedParams, setDetectedParams] = useState<any[]>([]);

  const xparams = watch('x1Parameters');
  const currentAffUrl = watch('affiliateUrl');
  const offerRedirectDomain = watch('redirectDomainId');
  const offerUniqueId = watch('offerUniqueId');
  const autoCreateTrafficSource = watch('autoCreateTrafficSource');
  const selectedTrafficSourceId = watch('selectedTrafficSourceId');

  useEffect(() => {
    initParams({ creatingNewSource: autoCreateTrafficSource, selectedSourceId: selectedTrafficSourceId, createdParams: xparams });
  }, [xparams, autoCreateTrafficSource, selectedTrafficSourceId, trafficSources]);

  const initParams = ({ creatingNewSource, selectedSourceId, createdParams }: { creatingNewSource: boolean; selectedSourceId: any; createdParams: any[] }) => {
    let params = createdParams;
    const source = trafficSources.find((source: any) => source._id === selectedSourceId);

    if (!creatingNewSource && source) {
      params = source.parameterOptions || [];
    }

    const reconstructed = new Map();

    for (const param of params) {
      const isPresent = paramInjected.get(param.tag) ?? false;

      reconstructed.set(param.tag, isPresent);
    }

    setParamInjected(reconstructed);
    setDetectedParams(params);

    resetUrl(reconstructed);
  };

  const resetUrl = (newParams: Map<string, boolean>) => {
    let newUrl = currentAffUrl;

    for (const [tag, isPresent] of Array.from(newParams.entries())) {
      newUrl = updateAffUrl(tag, isPresent);
    }

    setValue(name, newUrl);
  };

  const updateAffUrl = (tag: string, toAdd: boolean) => {
    if (!currentAffUrl) return '';

    let newUrl = currentAffUrl;

    if (toAdd) {
      newUrl = appendParameterToUrl(currentAffUrl, tag);
    } else {
      newUrl = removeParameterFromUrl(currentAffUrl, tag);
    }

    return newUrl;
  };

  const parameterToRawString = (urlSearchParams: URLSearchParams): string => {
    let str = '';

    for (const [key, value] of Array.from(urlSearchParams.entries())) {
      str += `${key}=${value}&`;
    }

    return str.slice(0, -1);
  };

  const removeParameterFromUrl = (currentUrl: string, parameterTag: string): string => {
    if (!currentUrl) return '';

    try {
      const url = new URL(currentUrl);
      url.searchParams.delete(parameterTag);

      const rawString = parameterToRawString(url.searchParams);

      const base = `${url.origin}${url.pathname}`;

      if (!rawString) return base;

      return `${base}?${rawString}`;
    } catch {
      return currentUrl;
    }
  };

  const appendParameterToUrl = (currentUrl: string, parameterTag: string): string => {
    if (!currentUrl) return '';

    try {
      const url = new URL(currentUrl);
      url.searchParams.set(parameterTag, `{${parameterTag}}`);

      const rawString = parameterToRawString(url.searchParams);
      const newUrl = `${url.origin}${url.pathname}?${rawString}`;
      return newUrl;
    } catch {
      return currentUrl;
    }
  };

  const addAffUrl = () => {
    const affUrlBase = getRedirectUrl(offerRedirectDomain, offerUniqueId);
    const newValue = `${currentAffUrl}${affUrlBase}`;
    setValue(name, newValue);
    trigger(name);
  };

  const handleParameterClick = (parameterTag: string, currentValue: boolean) => {
    const newUrl = updateAffUrl(parameterTag, !currentValue);
    setValue(name, newUrl);

    if (!newUrl) return;

    const newParams = new Map(paramInjected);
    newParams.set(parameterTag, !currentValue);
    setParamInjected(newParams);
  };

  const fieldError = (errors as any)[name];
  const errorMessage = (fieldError?.message as string) || helperText;
  const isError = !!fieldError;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return <TextField {...field} label={label} helperText={errorMessage} error={isError} fullWidth />;
        }}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            addAffUrl();
          }}
        >
          Offer Redirect Url
        </Button>
        {detectedParams.map((param: any) => {
          const currentValue = paramInjected.get(param.tag) ?? false;
          const variant = currentValue ? 'contained' : 'outlined';

          return (
            <Button
              key={param.tag}
              variant={variant}
              size="small"
              onClick={() => {
                handleParameterClick(param.tag, currentValue);
              }}
            >
              {param.tag}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};
